import { LABEL_MIXING_NOTE_MAX_LENGTH } from '../../constants';
import { getIsEvidenceValid } from '../../helpers/getIsEvidenceValid';
import { getPreselectedStatement } from '../../helpers/getPreselectedStatement';
import { getSuggestedCodes } from '../../helpers/getSuggestedCodes';
import { toDocumentViews } from '../../helpers/toDocumentViews';
import { useEffect, useMemo, useState } from 'react';

import type { StatementOption, UseReviewFormParams, UseReviewFormReturn } from './types';

// The reviewer's form state, shared by the modal and the /_visual preview so both
// exercise the same selection, contradiction and validity rules.
const useReviewForm = ({
    canVerify,
    isBusy,
    reviewState
}: UseReviewFormParams): UseReviewFormReturn => {
    const [selectedStatementKey, setSelectedStatementKey] = useState<string | null>(null);
    const [evidence, setEvidence] = useState('');
    const [labelMixingNote, setLabelMixingNote] = useState('');

    const documents = useMemo(
        () =>
            reviewState.kind === 'loaded'
                ? toDocumentViews(reviewState.review.documents, canVerify)
                : [],
        [reviewState, canVerify]
    );
    const statements = documents.flatMap(document => document.statements);
    const suggestedCodes = getSuggestedCodes(documents);
    const selectedStatement = statements.find(option => option.key === selectedStatementKey);
    const storedNote =
        reviewState.kind === 'loaded'
            ? (reviewState.review.current.verification?.labelMixingNote ?? '')
            : '';

    const selectStatement = (option: StatementOption | undefined) => {
        setSelectedStatementKey(option?.key ?? null);
        setEvidence(option?.excerpt ?? '');
    };

    // Re-seed when a review loads. Keeping the stored note prevents a re-confirmation
    // from erasing it: the backend upsert overwrites the column with what we send.
    useEffect(() => {
        selectStatement(getPreselectedStatement(documents));
        setLabelMixingNote(storedNote);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documents]);

    return {
        canClear:
            canVerify &&
            reviewState.kind === 'loaded' &&
            reviewState.review.current.source === 'VERIFIED',
        documents,
        evidence,
        hasContradiction: suggestedCodes.length > 1,
        isConfirmEnabled:
            canVerify &&
            !isBusy &&
            Boolean(selectedStatement) &&
            getIsEvidenceValid(evidence) &&
            labelMixingNote.trim().length <= LABEL_MIXING_NOTE_MAX_LENGTH,
        labelMixingNote,
        onEvidenceChange: setEvidence,
        onLabelMixingNoteChange: setLabelMixingNote,
        onSelectStatement: (key: string) =>
            selectStatement(statements.find(option => option.key === key && option.isSelectable)),
        selectedStatement,
        selectedStatementKey,
        suggestedCodes
    };
};

export default useReviewForm;
